// REPORTS-UI: the caller's own Friday report email (`reports.subscription`,
// `reports.setSubscription`; 13 §Friday report "Sending", coverage row 6 from the member's side).
// - `ReportEmailGroup`: Account › Notifications › "Friday report", one switch.
// - `ReportUnsubscribe`: `/c/{slug}/reports/unsubscribe`, the landing of the email footer's
//   "Stop getting this report" link: the state in one line and one button (never a change on
//   load: a mail scanner opening the link must not opt anyone out).
// Who: owners, admins and client viewers (the default recipients) with their own membership. An
// agency operator or a plain member is told why there is nothing to change. A manager's extra
// address that is also the caller's is said plainly: that address still gets the report.
import { ApiError } from "@companyos/sdk";
import { Button, Notice, SettingsGroup, SettingsRow, Switch, toast } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { MailIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isReader } from "../company";
import { problemText } from "../ops-parts";
import { LinkButton } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { SetupPage } from "../settings/setup-page";
import { useFeature } from "../use-feature";

type Subscription = Awaited<ReturnType<Api["client"]["reports"]["subscription"]>>;

export const reportEmailKeys = {
  mine: (co: string) => ["company", co, "report-subscription"] as const,
};

export const reportSubscriptionQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: reportEmailKeys.mine(co),
    queryFn: () => api.client.reports.subscription({ params: { co } }),
    retry: false,
  });

function useSetSubscription(api: Api, co: string, onDone?: (s: Subscription) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) =>
      api.client.reports.setSubscription({ params: { co }, body: { subscribed } }),
    onSuccess: (s) => {
      qc.setQueryData(reportEmailKeys.mine(co), s);
      onDone?.(s);
    },
  });
}

/** One line for the caller's state. */
export function subscriptionLine(s: Subscription, company: string): string {
  if (!s.eligible)
    return s.extra
      ? `A manager lists your address as an extra recipient of ${company}'s Friday report.`
      : `You do not get ${company}'s Friday report: it goes to owners, admins and client viewers.`;
  if (s.subscribed) return `You get ${company}'s Friday report by email every week.`;
  return s.extra
    ? `You opted out, but a manager also lists your address as an extra recipient, so it still arrives. Ask them to remove it.`
    : `You opted out of ${company}'s Friday report. Other recipients still get it.`;
}

/** Account › Notifications › Friday report (readers only; nothing when `kpi` is off). */
export function ReportEmailGroup({ api, company }: { api: Api; company: CompanyAccess }) {
  const kpi = useFeature("kpi", company.id);
  const shown = kpi.enabled && isReader(company);
  const mine = useQuery({ ...reportSubscriptionQuery(api, company.id), enabled: shown });
  const set = useSetSubscription(api, company.id, () => toast.add({ title: "Saved" }));
  if (!shown) return null;
  // An operator has no membership here: nothing of theirs to change.
  if (mine.error instanceof ApiError && mine.error.status === 403) return null;
  return (
    <SettingsGroup id="friday-report" title="Friday report" data-slot="report-email">
      {mine.isPending ? (
        <Loading label="Loading your report email" rows={1} />
      ) : mine.isError ? (
        <LoadError what="Your report email" error={mine.error} />
      ) : (
        <SettingsRow
          label="Weekly report email"
          hint={subscriptionLine(mine.data, company.name)}
          htmlFor="report-email-on"
        >
          <Switch
            id="report-email-on"
            checked={mine.data.subscribed}
            disabled={
              set.isPending || kpi.readOnly || (!mine.data.eligible && mine.data.subscribed)
            }
            onCheckedChange={(v) => set.mutate(Boolean(v))}
          />
        </SettingsRow>
      )}
      {set.isError ? (
        <Notice tone="danger" className="m-3">
          {problemText(set.error, "Your choice could not be saved.")}
        </Notice>
      ) : null}
    </SettingsGroup>
  );
}

/** The email link's landing: one sentence, one button, a way to every other setting. */
export function ReportUnsubscribe({ api, company }: { api: Api; company: CompanyAccess }) {
  const kpi = useFeature("kpi", company.id);
  const reader = isReader(company);
  const mine = useQuery({
    ...reportSubscriptionQuery(api, company.id),
    enabled: kpi.enabled && reader,
  });
  const set = useSetSubscription(api, company.id);
  const refused = !reader || (mine.error instanceof ApiError && mine.error.status === 403);
  return (
    <SetupPage
      crumbs={["Reports"]}
      icon={MailIcon}
      title="Friday report email"
      slot="report-unsubscribe"
    >
      {!kpi.enabled ? (
        <Notice>Reports are switched off in {company.name}: no Friday report is sent.</Notice>
      ) : refused ? (
        <Notice>
          Only owners, admins and client viewers of {company.name} get the Friday report, so there
          is nothing to change for you here.
        </Notice>
      ) : mine.isPending ? (
        <Loading label="Loading your report email" rows={2} />
      ) : mine.isError ? (
        <LoadError what="Your report email" error={mine.error} />
      ) : (
        <SettingsGroup title={company.name}>
          <div className="flex flex-col gap-3 px-4 py-3" data-slot="report-email-state">
            <p className="text-sm" role="status">
              {subscriptionLine(mine.data, company.name)}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {mine.data.subscribed && mine.data.eligible ? (
                <Button
                  size="sm"
                  disabled={set.isPending || kpi.readOnly}
                  onClick={() => set.mutate(false)}
                >
                  {set.isPending ? "Saving…" : "Stop sending it to me"}
                </Button>
              ) : null}
              {!mine.data.subscribed ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={set.isPending || kpi.readOnly}
                  onClick={() => set.mutate(true)}
                >
                  {set.isPending ? "Saving…" : "Send it to me again"}
                </Button>
              ) : null}
              <LinkButton
                render={
                  <Link
                    to="/c/$slug/notifications"
                    params={{ slug: company.slug }}
                    search={keepFocus}
                    hash="friday-report"
                  />
                }
              >
                All notification settings
              </LinkButton>
            </div>
            {kpi.readOnly ? (
              <p className="text-xs text-muted-foreground">
                Reports are read-only in {company.name} right now, and no report is sent.
              </p>
            ) : null}
            {set.isError ? (
              <Notice tone="danger">
                {problemText(set.error, "Your choice could not be saved.")}
              </Notice>
            ) : null}
          </div>
        </SettingsGroup>
      )}
    </SetupPage>
  );
}
